<script lang="ts">
  import { scale } from "svelte/transition"
  import { getContext } from "svelte"
  import { hexToRgba } from "lib/helpers"
  import licencePlateIcon from "../Icons/licence_plate_eu.svg?raw"
  import Svg from "../components/Svg.svelte"
  import ChatCard from "../components/ChatCard.svelte"
  import TextInput from "../../component_library/components/svelte/TextInput.svelte"
  import type { Theme } from "website_script/types/theme"
  import type { Settings } from "website_script/types/settings"
  import type { Texts } from "website_script/types/texts"
  import type { PromptComponentOnModalTriggerFunc } from "./types"
  import type { Environment } from "website_script/types/environment"

  const texts = getContext<Texts>("texts")
  const { trackFunction } = getContext<Environment>("environment")
  const { enableLicensePlateInput, licensePlateCountry } =
    getContext<Settings>("settings")
  const { accentColor, neutralTextColor } = getContext<Theme>("theme")

  export let onModalTrigger: PromptComponentOnModalTriggerFunc

  let lpnNumber: string = ""
  let showLpnSubmitButton: boolean
  $: showLpnSubmitButton = !!lpnNumber

  /**
   * Stores currently typed license plate number
   * Opens the modal on enter with given license plate number
   */
  const lpnKeydownHandler = (e: KeyboardEvent) => {
    if (e.keyCode === 13) {
      lpnCtaHandler()
    }
  }

  /**
   * Opens the modal on enter with given license plate number
   */
  const lpnCtaHandler = () => {
    trackClickOnElement("submit lpn")
    trackFunction({
      entity: "engagement-widget",
      element: "prompt: trade_in",
      action: lpnNumber ? "submitted" : "clicked",
      details: lpnNumber ? "LPN" : "blank LPN",
      type: "interacted_with_cta",
      flow: "trade_in",
    })
    onModalTrigger({ source: "Chat pop-up", vin_lpn: lpnNumber })
  }

  /**
   * Opens the modal on click inside of the chat pop-up
   */
  const elementClickHandler = (e: MouseEvent) => {
    if (e.target && e.target instanceof Element) {
      if (
        e.target.closest(".auws-chat-options") ||
        e.target.closest(".auws-lpn-input") ||
        e.target.closest(".auws-cta") ||
        e.target.closest(".au-su-footer-link")
      ) {
        return
      }

      const target = e.target
      const clickedElementClass = target.className
      let clickedElement = ""
      if (clickedElementClass.includes("auws-brand-input")) {
        clickedElement = "input"
      } else if (clickedElementClass.includes("auws-message")) {
        clickedElement = "text"
      } else {
        clickedElement = "element"
      }

      trackClickOnElement(clickedElement)
      onModalTrigger({ source: "Chat pop-up" })
    }
  }

  /**
   * Track click on specific element in the Chat pop-up
   */
  const trackClickOnElement = (el: string) => {
    trackFunction({
      entity: "engagement-widget",
      element: `prompt chat: ${el}`,
      action: "clicked",
      type: "interacted_with_cta",
      flow: "trade_in",
    })
  }
</script>

<!--
@component

This shows a small card that can be used for prompting the users to go through
the trade in flow
-->
<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div
  data-testid="trade-in-prompt"
  class="auws-chat-popup-container"
  style={`--accent-color: ${accentColor}; --faded-neutral-color: ${hexToRgba(
    neutralTextColor,
    0.5
  )}; --neutral-text-color: ${neutralTextColor}; --shadow-color: ${hexToRgba(
    neutralTextColor,
    0.35
  )}; --border-color: ${hexToRgba(
    neutralTextColor,
    1
  )}; --focused-border-color: ${hexToRgba(neutralTextColor, 0.75)}`}
  on:click={elementClickHandler}
>
  <ChatCard
    message={texts.tradeInPromptMessage}
    extraMessage={enableLicensePlateInput
      ? null
      : texts.tradeInPromptExtraMessage}
    sender={enableLicensePlateInput ? texts.promptSender : null}
  >
    <div slot="call-to-action">
      {#if enableLicensePlateInput}
        <div class="auws-lpn-container">
          <div class="auws-lpn-before">
            <div class="auws-license-plate-eu">
              <Svg
                data={licencePlateIcon}
                color="#FFE600"
                size="17px"
                alt="lpn"
              />
            </div>
            <div class="auws-license-plate-country">{licensePlateCountry}</div>
          </div>
          <input
            type="text"
            name="vin_lpn"
            class="auws-lpn-input"
            placeholder={texts.tradeInPromptLpnPlaceholder}
            on:keydown={lpnKeydownHandler}
            bind:value={lpnNumber}
          />
          {#if showLpnSubmitButton}
            <div
              class="auws-cta"
              transition:scale|global={{ duration: 300 }}
              on:click={() =>
                onModalTrigger({
                  source: "Submit LPN Button",
                  vin_lpn: lpnNumber,
                })}
            >
              <svg
                width="12"
                height="13"
                viewBox="0 0 18 19"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 9.5L0 19V11.4397L7.15547 9.5L0 7.56025V0L18 9.5Z"
                  fill="white"
                />
              </svg>
            </div>
          {/if}
        </div>
      {:else}
        <TextInput placeholder={texts.tradeInPromptPlaceholder} />
      {/if}
    </div>
  </ChatCard>
</div>

<style>
  .auws-lpn-container {
    width: 100%;
    position: relative;
    border: none;
  }
  .auws-lpn-before {
    position: absolute;
    background: #0946a0;
    z-index: 1;
    width: 33px;
    display: flex;
    height: 46px;
    margin-top: 1px;
    margin-left: 1px;
    border-radius: 2px 0px 0px 2px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .auws-license-plate-eu {
    display: flex;
  }
  .auws-license-plate-country {
    color: #ffffff;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    text-transform: uppercase;
  }
  .auws-lpn-input {
    box-sizing: border-box;
    transition: all 0.35s;
    position: relative;
    margin-bottom: 0px;
    width: 100%;
    border-radius: 3px;
    padding: 12px;
    font-size: 16px;
    cursor: pointer;
    background: white;
    height: 48px;
    padding-left: 45px !important;
    border: 1px solid var(--border-color);
    color: var(--text-color);
  }
  .auws-lpn-input:focus {
    outline-style: none;
    border-color: var(--focused-border-color);
  }
  .auws-lpn-input::placeholder,
  .auws-lpn-input::-webkit-input-placeholder {
    color: var(--faded-neutral-color);
    font-size: 16px;
  }
  .auws-cta {
    position: absolute;
    top: 9px;
    right: 12px;
    cursor: pointer;
    background: var(--neutral-text-color);
    box-shadow: 0px 1px 4px var(--shadow-color);
    border-radius: 4px;
    padding: 5px;
    width: 30px;
    height: 30px;
    box-sizing: border-box;
  }
  .auws-cta svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-40%, -60%) rotate(-35deg);
  }
</style>
